import { useEffect, useRef, useState } from 'react';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useHeidiSession, HeidiSessionStatus } from '@components/Clinicians/Session/hooks/useHeidiSession';
import { useHeidiEnabled } from './useHeidiEnabled';
import { useAudioTranscription } from './useAudioTranscription';
import { useSessionAppointment } from './useSessionAppointment';
import useHeidiError from './useHeidiError';
import useAppointmentDetails from './appointments/useAppointmentDetails';

export type TranscriptStatus = 'live' | 'processing' | 'ready' | 'not_live' | 'paused' | 'review';

export type TranscriptErrorStatus =
  | 'polling_transcript_error'
  | 'official_transcript_error'
  | 'fetch_backup_transcript_error'
  | 'no_transcription_service_error'
  | null;

export interface UseTranscriptResult {
  transcriptText: string;
  isLoading: boolean;
  error: TranscriptErrorStatus;
  hasTranscriptGaps: boolean;
  status: string;
}

interface TranscriptChunk {
  id: number;
  status: string;
  transcript: string | null;
  duration: number;
}

interface TranscriptResponse {
  live?: { id: string; chunks?: TranscriptChunk[] | null; transcript: string | null }[] | null;
  transcript?: string | null;
}

const completedChunks = (response?: TranscriptResponse | null): TranscriptChunk[] =>
  (response?.live?.[0]?.chunks ?? []).filter((chunk) => chunk.status === 'COMPLETED');

const stitchChunks = (response?: TranscriptResponse | null): string | null => {
  const text = completedChunks(response)
    .sort((a, b) => a.id - b.id)
    .map((chunk) => chunk.transcript?.trim())
    .filter((part): part is string => !!part);

  return text.length ? text.join('\n') : null;
};

const selectTranscript = (response?: TranscriptResponse | null): string | null =>
  response?.transcript || stitchChunks(response) || null;

const useAINotesRefetchInterval = (appointmentId: number) => {
  const dispatch = useDispatch();

  const { data } = useQuery({
    queryKey: ['getAINotesRefetchInterval', appointmentId],
    queryFn: () => dispatch(actions.getAIMeta(appointmentId)),
    enabled: !!appointmentId
  });

  return data?.ai_notes_meta?.[0]?.transcript_delay_size ?? 10;
};

const useBackupTranscript = (appointmentId: number) => {
  const dispatch = useDispatch();

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['useBackupTranscript', appointmentId],
    queryFn: () => dispatch(actions.getBackupTranscript(appointmentId)),
    select: (data) => data?.transcript ?? null,
    enabled: false
  });

  return {
    backupTranscript: data,
    backupTranscriptRefetch: refetch,
    isBackupTranscriptLoading: isLoading
  };
};

const storeSessionTranscript = (transcript: string, appointmentId: number) => {
  if (['undefined', null].includes(transcript)) {
    sessionStorage.setItem(`heidi-transcript-${appointmentId}`, 'null');
    return;
  }
  sessionStorage.setItem(`heidi-transcript-${appointmentId}`, transcript);
};

const useTranscript = (appointmentId: number): UseTranscriptResult => {
  const dispatch = useDispatch();
  const aiNotesEnabled = useHeidiEnabled(appointmentId);
  const refetchInterval = useAINotesRefetchInterval(appointmentId);

  const { status, sessionId, hasHeidiError } = useHeidiSession();
  const { isAudioError, isChunkStoreError, latestChunk, isChunkUploadSuccess } = useAudioTranscription();
  const { reportError, reportSuccess, isPollingError, isActualError } = useHeidiError();
  const { backupTranscript, backupTranscriptRefetch, isBackupTranscriptLoading } = useBackupTranscript(appointmentId);
  const { detailsCurrentAppointment } = useAppointmentDetails(appointmentId);
  const {
    appointment: { appointment_completed: isApptCompleted, appt_type_code: apptType }
  } = useSessionAppointment();

  const isAudit = apptType === 'AUDIT';

  const [transcriptStatus, setTranscriptStatus] = useState<TranscriptStatus>('not_live');
  const [transcriptErrorCode, setTranscriptErrorCode] = useState<TranscriptErrorStatus>(null);
  const [endpointCounter, setEndpointCounter] = useState<number>(0);
  const [firstChunkExists, setFirstChunkExists] = useState<boolean>(
    !!sessionStorage.getItem(`first-chunk-exists-${appointmentId}`)
  );

  const hasCompletedChunksRef = useRef(false);

  const currentStoredTranscript = sessionStorage.getItem(`heidi-transcript-${appointmentId}`);
  const isTranscriptionActivated = detailsCurrentAppointment?.ai_session_id;

  const shouldPoll =
    !isAudit && !isAudioError && status !== 'completed' && !isApptCompleted && !!latestChunk && !hasHeidiError;
  const isCurrentTranscriptStored = currentStoredTranscript && currentStoredTranscript !== 'null';
  const shouldManuallyRefetch =
    !isAudit &&
    !isCurrentTranscriptStored &&
    isTranscriptionActivated &&
    (isApptCompleted || ['paused', 'completed'].includes(status) || (status === 'active' && firstChunkExists));

  useEffect(() => {
    if (isAudit && !isCurrentTranscriptStored && !isBackupTranscriptLoading) {
      backupTranscriptRefetch();
    }
  }, [isAudit, appointmentId, isCurrentTranscriptStored]);

  useEffect(() => {
    if (isAudit && backupTranscript) {
      try {
        storeSessionTranscript(JSON.parse(backupTranscript).json, appointmentId);
      } catch {
        storeSessionTranscript(backupTranscript, appointmentId);
      }
    }
  }, [backupTranscript, isAudit, appointmentId]);

  const {
    data: latestTranscript,
    isLoading: isLatestTranscriptLoading,
    refetch: latestTranscriptRefetch
  } = useQuery({
    queryKey: ['useTranscript', appointmentId],
    queryFn: async () => {
      setEndpointCounter((prev) => prev + 1);

      const data = await dispatch(actions.getAITranscript(appointmentId));
      const response = data?.transcript?.transcript?.response;

      hasCompletedChunksRef.current = completedChunks(response).length > 0;

      return data;
    },
    refetchInterval: shouldPoll && refetchInterval * 1000,
    refetchIntervalInBackground: shouldPoll,
    refetchOnWindowFocus: false,
    select: (data) => selectTranscript(data.transcript?.transcript?.response),
    enabled: !!appointmentId && !!aiNotesEnabled && !!sessionId && !hasHeidiError && firstChunkExists
  });

  useEffect(() => {
    if (isAudit) return;
    if (['idle', 'completed'].includes(status) || isApptCompleted) return;
    !latestTranscript && !hasCompletedChunksRef.current && status !== 'paused'
      ? reportError()
      : reportSuccess();

    if (latestTranscript) storeSessionTranscript(latestTranscript, appointmentId);
  }, [latestTranscript, endpointCounter, status, isApptCompleted, appointmentId]);

  useEffect(() => {
    if (!shouldManuallyRefetch) return;
    if (!hasHeidiError) latestTranscriptRefetch();

    if (latestTranscript) {
      storeSessionTranscript(latestTranscript, appointmentId);
    } else {
      backupTranscriptRefetch();
      storeSessionTranscript(backupTranscript ? JSON.parse(backupTranscript).json : 'null', appointmentId);
    }
  }, [isApptCompleted, status, latestTranscript, backupTranscript, hasHeidiError, isTranscriptionActivated]);

  const isTranscriptLoading = isLatestTranscriptLoading || isBackupTranscriptLoading;

  const getTranscriptErrorCode = (): TranscriptErrorStatus => {
    if (isTranscriptLoading) return null;
    if (isApptCompleted && !isCurrentTranscriptStored && isTranscriptionActivated)
      return 'fetch_backup_transcript_error';
    if (isApptCompleted && !isTranscriptionActivated && !hasHeidiError) return 'no_transcription_service_error';
    if (!isApptCompleted && isPollingError && !hasHeidiError) return 'polling_transcript_error';
    if (hasHeidiError) return 'official_transcript_error';
    return null;
  };

  useEffect(() => {
    const errorCode = getTranscriptErrorCode();
    setTranscriptErrorCode(errorCode);
  }, [isApptCompleted, isPollingError, hasHeidiError, isActualError, currentStoredTranscript, isTranscriptLoading]);

  const syncTranscriptStatus = (newStatus?: HeidiSessionStatus) => {
    const statusMap: Record<HeidiSessionStatus, TranscriptStatus> = {
      idle: 'ready',
      paused: 'paused',
      active: 'live',
      processing: 'processing',
      completed: 'review'
    };

    setTranscriptStatus(newStatus ? statusMap[newStatus] : 'not_live');
  };

  useEffect(() => {
    syncTranscriptStatus(status);
  }, [status]);

  useEffect(() => {
    if (!isChunkUploadSuccess.isBackend) return;
    if (sessionStorage.getItem(`first-chunk-exists-${appointmentId}`) !== 'true') {
      sessionStorage.setItem(`first-chunk-exists-${appointmentId}`, 'true');
      setFirstChunkExists(true);
    }
  }, [isChunkUploadSuccess]);

  return {
    transcriptText: currentStoredTranscript?.replaceAll(/\n/g, '\n\n') ?? 'null',
    isLoading: isTranscriptLoading,
    hasTranscriptGaps: isChunkStoreError && !hasHeidiError,
    error: transcriptErrorCode,
    status: transcriptStatus
  };
};

export default useTranscript;
